import {
  Globe,
  Lock,
  Network,
  ShieldCheck,
  ShieldAlert,
  Database,
  Activity,
  Gauge,
  type LucideIcon,
} from 'lucide-react';
import type {
  WatchpointType,
  WatchpointStatus,
  AlarmSeverity,
  AlarmEventStatus,
} from '@/api/healthcheck';
import { formatDateTime } from '@/lib/format-date';

export interface TypeMeta {
  label: string;
  icon: LucideIcon;
  group: 'protocol' | 'data';
  blurb: string;
}

export const WATCHPOINT_TYPES: Record<WatchpointType, TypeMeta> = {
  http: { label: 'HTTP', icon: Globe, group: 'protocol', blurb: 'HTTP endpoint status, body & latency.' },
  https: { label: 'HTTPS', icon: Lock, group: 'protocol', blurb: 'HTTPS endpoint status, body & latency.' },
  tcp: { label: 'TCP', icon: Network, group: 'protocol', blurb: 'TCP port reachability.' },
  dns: { label: 'DNS', icon: Network, group: 'protocol', blurb: 'DNS record resolution & expected values.' },
  tls: { label: 'TLS / SSL', icon: ShieldCheck, group: 'protocol', blurb: 'Certificate validity & expiry.' },
  elastic: { label: 'Elastic (WAF logs)', icon: Database, group: 'data', blurb: 'Elasticsearch WAF-log aggregation.' },
  brobe: { label: 'Brobe', icon: Activity, group: 'data', blurb: 'Custom probe-engine query.' },
  waap: { label: 'WAAP', icon: ShieldAlert, group: 'data', blurb: 'WAAP security dashboard metric.' },
  delivery: { label: 'CDN Delivery', icon: Gauge, group: 'data', blurb: 'CDN delivery metric.' },
};

export function typeMeta(type: WatchpointType): TypeMeta {
  return WATCHPOINT_TYPES[type] ?? { label: type, icon: Activity, group: 'data', blurb: '' };
}

/** Monitor up/down tone (a `StatusPill` tone). `null`/unknown → muted. */
export function statusTone(
  status?: WatchpointStatus | string | null,
): 'success' | 'danger' | 'muted' {
  const s = String(status ?? '').toLowerCase();
  if (s === 'up') return 'success';
  if (s === 'down') return 'danger';
  return 'muted';
}

export function statusLabel(status?: WatchpointStatus | string | null, active = true): string {
  if (!active) return 'Paused';
  const s = String(status ?? '').toLowerCase();
  if (s === 'up') return 'Up';
  if (s === 'down') return 'Down';
  return 'Pending';
}

/** Alarm severity → `Badge` tone. */
export function severityTone(severity: AlarmSeverity): 'danger' | 'warning' | 'info' {
  if (severity === 'critical') return 'danger';
  if (severity === 'warning') return 'warning';
  return 'info';
}

/** Incident lifecycle → `Badge` tone. */
export function eventStatusTone(status: AlarmEventStatus): 'danger' | 'warning' | 'success' {
  if (status === 'triggered') return 'danger';
  if (status === 'acknowledged') return 'warning';
  return 'success';
}

/** Subscriber delivery status → `Badge` / `StatusPill` tone. */
export function subscriberStatusTone(status: string): 'warning' | 'success' | 'neutral' {
  if (status === 'verified') return 'success';
  if (status === 'disabled') return 'neutral';
  return 'warning'; // pending + unknown
}

// ---- Formatters ----------------------------------------------------------

/** pgtype.Numeric can arrive as a string — coerce to a finite number or null. */
export function toNum(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

export function fmtPercent(v: number | string | null | undefined, digits = 2): string {
  const n = toNum(v);
  return n === null ? '—' : `${n.toFixed(digits)}%`;
}

export function fmtLatency(v: number | string | null | undefined): string {
  const n = toNum(v);
  if (n === null) return '—';
  return n >= 1000 ? `${(n / 1000).toFixed(2)} s` : `${Math.round(n)} ms`;
}

/** Check interval / timeout in seconds → human ("30s", "5m", "1h"). */
export function fmtSeconds(s: number | null | undefined): string {
  const n = toNum(s);
  if (n === null || n <= 0) return '—';
  if (n < 60) return `${n}s`;
  if (n < 3600) return `${Math.round(n / 60)}m`;
  return `${Math.round(n / 3600)}h`;
}

/** Compact relative time ("just now", "5m ago", "2h ago", "3d ago"). */
export function fmtRelTime(iso?: string | null): string {
  if (!iso) return 'Never';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const diff = Date.now() - t;
  if (diff < 0) return 'just now';
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

// Delegates to the shared formatter (consistent app-wide format + zero-date guard).
export const fmtDateTime = formatDateTime;

/** Short, readable summary of a monitor's target from its config. */
export function targetSummary(type: WatchpointType, config: Record<string, unknown>): string {
  const c = config ?? {};
  switch (type) {
    case 'http':
    case 'https':
      return String(c.url ?? '—');
    case 'tcp':
      return c.host ? `${c.host}:${c.port ?? ''}` : '—';
    case 'dns':
      return c.host ? `${c.host} (${c.record_type ?? 'A'})` : '—';
    case 'tls':
      return c.host ? `${c.host}:${c.port ?? 443}` : '—';
    case 'elastic':
    case 'brobe':
      return String(c.query ?? '—');
    case 'waap':
    case 'delivery': {
      const domains = Array.isArray(c.domains) ? (c.domains as string[]).join(', ') : '';
      return domains || String(c.customer_id ?? '—');
    }
    default:
      return '—';
  }
}
